/**
 * Dates the way a person says them, rather than the way a report files them.
 *
 * Apple sends plain calendar days — `2026-08-09`, no time and no zone — and
 * `2026-08-09 → 2026-09-07` is a filename, not a sentence. Everything on the
 * dashboard that prints a date comes through here so that a date does not
 * change shape between one card and the next.
 *
 * `en-GB` and `medium` match `postDate` in `posts.ts`, which got here first.
 * Note that `en-GB` abbreviates September to "Sept", alone among the twelve —
 * that is the locale's spelling, not a typo to be patched out.
 *
 * ⚠️ `timeZone: 'UTC'` is the part that matters, and the part that is invisible
 * when it is wrong. `new Date('2026-09-07')` is UTC midnight; formatted in a
 * zone west of Greenwich without this, every date on the screen prints as the
 * day before. Nobody developing in Asia would ever see it.
 */
const DAY = new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeZone: 'UTC' });
const DAY_IN_YEAR = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
});

/** "9 Aug 2026". An unreadable date prints itself, not the words "Invalid Date". */
export const day = (iso: string): string => {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? iso : DAY.format(at);
};

/** "9 Aug" — for the chart axis, where four labels share the width of the card. */
export const dayInYear = (iso: string): string => {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? iso : DAY_IN_YEAR.format(at);
};

/**
 * Two dates as one span, saying each shared part once.
 *
 * The three shapes are the three answers to "how far apart are they":
 *
 * - same month — `3 – 7 Sept 2026`, which is most of this screen, since the
 *   7-day and month-to-date periods rarely leave one month;
 * - same year  — `9 Aug – 7 Sept 2026`;
 * - neither    — `9 Dec 2025 – 7 Jan 2026`, the one span where dropping the
 *   first year would make the range ambiguous rather than tidier.
 *
 * Both ends are null until a first report lands, and the dashboard shows its
 * empty state instead of any of this — so that guard is here for the type, not
 * for a screen anybody sees. It still beats interpolating the two straight
 * into a template, which is what printed `null → null` if it were ever wrong
 * about that.
 */
export const dateRange = (from: string | null, to: string | null): string => {
  if (from === null || to === null) return 'No days yet';
  if (from === to) return day(to);

  const start = new Date(from);
  const end = new Date(to);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return `${from} – ${to}`;

  const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
  const sameMonth = sameYear && start.getUTCMonth() === end.getUTCMonth();
  const left = sameMonth
    ? String(start.getUTCDate())
    : sameYear
      ? DAY_IN_YEAR.format(start)
      : DAY.format(start);
  return `${left} – ${DAY.format(end)}`;
};

/**
 * A moment — a real timestamp with a zone — as the day it fell on **where
 * the reader is**. Not `day()`: that one pins UTC on purpose, for calendar
 * days Apple sends with no zone, and printed a second factor turned on at
 * 00:01 Saigon time as the day before. Found by QA. `zone` is for tests;
 * the browser's own is the default.
 */
export const moment = (iso: string, zone?: string): string => {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    ...(zone ? { timeZone: zone } : {}),
  }).format(at);
};
